import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { usePlayerSurfaceNav } from '../../hooks/usePlayerSurfaceNav';
import { getDailyQuests } from '../../api/quests';
import { useLegacyAchievements } from '../../hooks/usePlayerState';
import { getHonorBalance } from '../../api/honor';
import { useTokens } from '../../context/TokenContext';
import { useXP } from '../../context/XPContext';
import { useHealth } from '../../context/HealthContext';
import apiClient from '../../api/client';
import TokenIcon from '../TokenIcon';
import ElixirAmount from '../ElixirAmount';
import { useGlobalConceptBuilder } from '../../context/ConceptBuilderContext';
import useForgeableSignal from '../../hooks/useForgeableSignal';
import useActiveContext from '../../hooks/useActiveContext';
import './BottomLeftStatusRail.css';

function getQuestIcon(iconType) {
  switch (iconType) {
    case 'learn':
      return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" />
          <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" />
        </svg>
      );
    case 'train':
      return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10" />
          <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
          <line x1="12" y1="17" x2="12.01" y2="17" />
        </svg>
      );
    case 'review':
      return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="6" width="18" height="13" rx="2" />
          <path d="M3 10h18" />
        </svg>
      );
    default:
      return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10" />
          <line x1="12" y1="8" x2="12" y2="12" />
          <line x1="8" y1="12" x2="16" y2="12" />
        </svg>
      );
  }
}

function getAccessToken() {
  if (typeof window === 'undefined') return '';
  return localStorage.getItem('access_token') || '';
}

function useCarouselHeightVar(pathname) {
  useEffect(() => {
    const FALLBACK_H = 120;
    const root = document.documentElement;
    let ro = null;
    let observed = null;
    let frame = null;

    const measure = () => {
      const el = document.querySelector('.concept-carousel-section');
      if (el) {
        const rect = el.getBoundingClientRect();
        const h = Math.max(Math.round(rect.height), 80);
        const bottomOffset = Math.max(0, Math.round(window.innerHeight - rect.bottom));
        root.style.setProperty('--carousel-height', `${h}px`);
        root.style.setProperty('--carousel-bottom-offset', `${bottomOffset}px`);
      } else {
        root.style.setProperty('--carousel-height', `${FALLBACK_H}px`);
        root.style.setProperty('--carousel-bottom-offset', '0px');
      }

      if (el !== observed) {
        if (ro && observed) ro.unobserve(observed);
        observed = el;
        if (ro && el) ro.observe(el);
      }
    };

    ro = new ResizeObserver(() => {
      if (frame) cancelAnimationFrame(frame);
      frame = requestAnimationFrame(measure);
    });

    measure();
    const t1 = setTimeout(measure, 120);
    const t2 = setTimeout(measure, 600);
    const t3 = setTimeout(measure, 1500);

    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, { passive: true });

    return () => {
      if (ro) ro.disconnect();
      if (frame) cancelAnimationFrame(frame);
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure);
    };
  }, [pathname]);
}

function BottomLeftStatusRail() {
  const location = useLocation();
  const navigate = useNavigate();
  const { open: openPlayerSurface } = usePlayerSurfaceNav();
  const renderToken = getAccessToken();
  const [portalTarget, setPortalTarget] = useState(null);
  useCarouselHeightVar(location.pathname);
  const [accessToken, setAccessToken] = useState(renderToken);
  const [dailyQuests, setDailyQuests] = useState([]);
  // Legacy achievements come from the shared player-state hook layer —
  // dedup'd with AchievementsOrb / PlayerShop / PlayerStats.
  const { data: achievements = [] } = useLegacyAchievements({ enabled: !!accessToken });
  const [activeTab, setActiveTab] = useState(null);
  const fetchIdRef = useRef(0);
  const { tokens, animating } = useTokens();
  const { xpStatus } = useXP();
  const { hp, maxHp, isDead, passiveFountains, hpMaskThreshold, overworldInvincible } = useHealth();
  const conceptBuilder = useGlobalConceptBuilder();
  const { hasForgeable } = useForgeableSignal(conceptBuilder.surfaceContext, conceptBuilder.lastCardSavedAt);
  const activeContext = useActiveContext();
  const [actionTab, setActionTab] = useState(null);
  const toggleActionTab = (tab) => {
    setActiveTab(null);
    setActionTab((current) => (current === tab ? null : tab));
  };
  const [honor, setHonor] = useState(() => {
    try {
      const cached = sessionStorage.getItem('global_honor');
      if (cached) return JSON.parse(cached).total_honor || 0;
    } catch {}
    return 0;
  });
  const [cardCounts, setCardCounts] = useState(() => {
    try {
      const cached = sessionStorage.getItem('global_card_counts');
      if (cached) return JSON.parse(cached);
    } catch {}
    return { active: 0, mastered: 0, inactive: 0, golden: 0, total: 0 };
  });
  const statsPollRef = useRef(null);

  useEffect(() => {
    setPortalTarget(document.querySelector('.bottom-left-stack'));
  }, [location.pathname]);

  useEffect(() => {
    if (renderToken !== accessToken) {
      setAccessToken(renderToken);
    }
  }, [accessToken, renderToken]);

  useEffect(() => {
    const syncAccessToken = () => {
      setAccessToken(getAccessToken());
    };

    window.addEventListener('storage', syncAccessToken);
    window.addEventListener('focus', syncAccessToken);

    return () => {
      window.removeEventListener('storage', syncAccessToken);
      window.removeEventListener('focus', syncAccessToken);
    };
  }, []);

  useEffect(() => {
    const requestId = fetchIdRef.current + 1;
    fetchIdRef.current = requestId;

    if (!accessToken) {
      // Achievements cleared automatically via the
      // `enabled: !!accessToken` guard on the useLegacyAchievements hook.
      setDailyQuests([]);
      setActiveTab(null);
      return undefined;
    }

    let cancelled = false;

    const loadRailData = async () => {
      // Achievements fetched via useLegacyAchievements hook; just the
      // quests still need a local fetch here.
      const questResponse = await getDailyQuests().catch(() => ({ data: [] }));
      if (cancelled || fetchIdRef.current !== requestId) return;
      setDailyQuests(Array.isArray(questResponse?.data) ? questResponse.data : []);
    };

    loadRailData();

    return () => {
      cancelled = true;
    };
  }, [accessToken]);

  useEffect(() => {
    if (!accessToken) return undefined;
    const fetchHonor = () => {
      getHonorBalance().then((data) => setHonor(data.total_honor || 0)).catch(() => {});
    };
    const fetchCards = () => {
      apiClient.get('/api/card-progression/').then((res) => {
        const counts = { active: 0, mastered: 0, inactive: 0, golden: 0, total: 0 };
        (res.data || []).forEach((card) => {
          const s = card.card_status;
          if (s === 'active') counts.active += 1;
          else if (s === 'mastered') counts.mastered += 1;
          else if (s === 'inactive') counts.inactive += 1;
          else if (s === 'golden') counts.golden += 1;
          counts.total += 1;
        });
        // Idempotent: the 60s poll builds a fresh counts object every time.
        // Committing it unconditionally re-renders the whole status rail each
        // minute (glassmorphism re-composite = the "once a minute" flicker,
        // Edward 2026-06-20). Only commit when a count actually changed.
        setCardCounts((prev) => (
          prev
          && prev.active === counts.active
          && prev.mastered === counts.mastered
          && prev.inactive === counts.inactive
          && prev.golden === counts.golden
          && prev.total === counts.total
        ) ? prev : counts);
      }).catch(() => {});
    };
    const hasCached = sessionStorage.getItem('global_card_counts') || sessionStorage.getItem('global_honor');
    const timeout = setTimeout(() => { fetchHonor(); fetchCards(); }, hasCached ? 3000 : 0);
    statsPollRef.current = setInterval(() => { fetchHonor(); fetchCards(); }, 60000);
    return () => { clearTimeout(timeout); clearInterval(statsPollRef.current); };
  }, [accessToken]);

  const nextAchievements = useMemo(() => {
    if (!achievements.length) return [];
    return achievements
      .filter((achievement) => !achievement.earned && achievement.threshold > 0)
      .map((achievement) => ({
        ...achievement,
        pct: Math.min(100, Math.round((achievement.progress / achievement.threshold) * 100)),
      }))
      .sort((left, right) => right.pct - left.pct || left.threshold - right.threshold)
      .slice(0, 3);
  }, [achievements]);

  const resolvedTab = activeTab;
  const toggleTab = (tab) => {
    setActionTab(null);
    setActiveTab((current) => (current === tab ? null : tab));
  };

  const level = xpStatus.level || 0;
  const xpProgress = Number(xpStatus.level_progress_pct) || 0;
  const xpPct = Math.max(0, Math.min(100, Math.round(xpProgress <= 1 ? xpProgress * 100 : xpProgress)));
  const xpCurrent = xpStatus.xp_for_current_level || 0;
  const xpNeeded = xpStatus.xp_needed_for_next || 0;
  const honorDisplay = Math.min(Math.floor(honor || 0), 9001);

  if (!accessToken || !portalTarget) {
    return null;
  }

  const renderProfileContent = () => (
    <>
      <div className="profile-row profile-row--level">
        <span className="profile-row-level-badge">Lv {level}</span>
        <div className="profile-row-bar">
          <div className="profile-row-bar-fill" style={{ width: `${xpPct}%` }} />
        </div>
        <span className="profile-row-value">{xpCurrent}/{xpNeeded}</span>
      </div>
      <div className="profile-row">
        <div className="profile-row-icon profile-row-icon--honor">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polygon points="12 2 15 9 22 9.5 17 14 18.5 21 12 17.5 5.5 21 7 14 2 9.5 9 9 12 2" />
          </svg>
        </div>
        <span className="profile-row-label">Honor</span>
        <span className="profile-row-value">{honorDisplay}</span>
      </div>
      <div className={`profile-row profile-row--elixir${animating ? ' profile-row--pop' : ''}`}>
        <div className="profile-row-icon profile-row-icon--elixir">
          <TokenIcon size={14} />
        </div>
        <span className="profile-row-label">Elixir</span>
        <ElixirAmount value={tokens} className="profile-row-elixir-amount" />
      </div>
      <div
        className="profile-row profile-row--cards"
        onClick={() => openPlayerSurface('/bank/drugs')}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === 'Enter' && openPlayerSurface('/bank/drugs')}
        aria-label="Open Pharmacy"
      >
        <div className="profile-row-icon profile-row-icon--cards">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="2" y="4" width="20" height="16" rx="2" />
            <path d="M2 8h20" />
          </svg>
        </div>
        <span className="profile-row-label">Cards</span>
        <span className="profile-row-value">{cardCounts.total}</span>
      </div>
      <div className="profile-breakdown">
        <span className="profile-card-stat" role="img" aria-label={`Golden ${cardCounts.golden}`}>
          <span className="profile-card-dot" style={{ background: '#fbbf24', boxShadow: '0 0 6px rgba(251,191,36,0.6)' }} />
          <span className="profile-card-count">{cardCounts.golden}</span>
        </span>
        <span className="profile-card-stat" role="img" aria-label={`Mastered ${cardCounts.mastered}`}>
          <span className="profile-card-dot" style={{ background: '#a855f7', boxShadow: '0 0 6px rgba(168,85,247,0.6)' }} />
          <span className="profile-card-count">{cardCounts.mastered}</span>
        </span>
        <span className="profile-card-stat" role="img" aria-label={`Active ${cardCounts.active}`}>
          <span className="profile-card-dot" style={{ background: '#22c55e', boxShadow: '0 0 6px rgba(34,197,94,0.6)' }} />
          <span className="profile-card-count">{cardCounts.active}</span>
        </span>
        <span className="profile-card-stat" role="img" aria-label={`Inactive ${cardCounts.inactive}`}>
          <span className="profile-card-dot" style={{ background: '#ef4444', boxShadow: '0 0 6px rgba(239,68,68,0.5)' }} />
          <span className="profile-card-count">{cardCounts.inactive}</span>
        </span>
      </div>
    </>
  );

  const renderQuestContent = () => (
    <>
      {dailyQuests.map((quest) => {
        const pct = quest.target > 0
          ? Math.min(100, Math.round((quest.progress / quest.target) * 100))
          : 0;
        return (
          <div
            key={quest._id || quest.quest_type}
            className={`map-quest-chip${quest.completed ? ' map-quest-chip--done' : ''}`}
            role="img"
            aria-label={[quest.action, quest.detail].filter(Boolean).join(': ') || 'Quest'}
          >
            <div className={`map-quest-chip-icon ${quest.icon || quest.quest_type}`}>
              {getQuestIcon(quest.icon || quest.quest_type)}
            </div>
            <div className="map-quest-chip-body">
              <span className="map-quest-chip-name">{quest.action || 'Quest'}</span>
              <div className="map-quest-chip-bar">
                <div className="map-quest-chip-bar-fill" style={{ width: `${pct}%` }} />
              </div>
            </div>
            <span className="map-quest-chip-bounty">
              {quest.completed ? <span className="map-quest-chip-check">&#10003;</span> : <ElixirAmount value={quest.bounty} />}
            </span>
          </div>
        );
      })}
    </>
  );


  const actionPopover = actionTab === 'upload' ? (
    <div className="bls-action-popover bls-action-popover--upload">
      <button
        type="button"
        className="map-upload-chip"
        onClick={() => {
          const input = document.getElementById('ppt-file-upload');
          if (input) {
            input.value = '';
            input.click();
          } else {
            navigate('/upload');
          }
          setActionTab(null);
        }}
      >
        <div className="map-upload-chip-icon">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
            <polyline points="14 2 14 8 20 8" />
            <line x1="12" y1="18" x2="12" y2="12" />
            <polyline points="9 15 12 12 15 15" />
          </svg>
        </div>
        <div className="map-upload-chip-body">
          <span className="map-upload-chip-name">New Upload</span>
          <span className="map-upload-chip-sub">Single deck</span>
        </div>
      </button>
      <button
        type="button"
        className="map-upload-chip map-upload-chip--disabled"
        disabled
        aria-label="Batch Upload, temporarily unavailable"
      >
        <div className="map-upload-chip-icon">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="3" width="7" height="7" rx="1" />
            <rect x="14" y="3" width="7" height="7" rx="1" />
            <rect x="3" y="14" width="7" height="7" rx="1" />
            <rect x="14" y="14" width="7" height="7" rx="1" />
          </svg>
        </div>
        <div className="map-upload-chip-body">
          <span className="map-upload-chip-name">Batch Upload</span>
          <span className="map-upload-chip-sub">Multiple decks</span>
        </div>
      </button>
      <button
        type="button"
        className="map-upload-chip map-upload-chip--disabled"
        disabled
        aria-label="Upload History, temporarily unavailable"
      >
        <div className="map-upload-chip-icon">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" />
            <polyline points="12 6 12 12 16 14" />
          </svg>
        </div>
        <div className="map-upload-chip-body">
          <span className="map-upload-chip-name">Upload History</span>
          <span className="map-upload-chip-sub">Past uploads</span>
        </div>
      </button>
    </div>
  ) : actionTab === 'hp' ? (
    <div className="bls-action-popover bls-action-popover--hp">
      <div className="map-hp-chip">
        <div className="map-hp-chip-icon">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78L12 21.23l8.84-8.84a5.5 5.5 0 0 0 0-7.78z" />
          </svg>
        </div>
        <div className="map-hp-chip-body">
          <span className="map-hp-chip-name">Current HP</span>
          <div className="map-hp-chip-bar">
            <div className="map-hp-chip-bar-fill" style={{ width: `${maxHp > 0 ? Math.min(100, (hp / maxHp) * 100) : 0}%` }} />
          </div>
        </div>
        <span className="map-hp-chip-value">{hp}/{maxHp}</span>
      </div>
      {passiveFountains && passiveFountains.passive_fountain_count > 0 && (
        <div className="map-hp-chip">
          <div className="map-hp-chip-icon">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
            </svg>
          </div>
          <div className="map-hp-chip-body">
            <span className="map-hp-chip-name">Regen</span>
            <span className="map-hp-chip-sub">{passiveFountains.passive_fountain_count} dialysis node{passiveFountains.passive_fountain_count !== 1 ? 's' : ''}</span>
          </div>
          <span className="map-hp-chip-value">+{passiveFountains.passive_heal_per_hour}/hr</span>
        </div>
      )}
      {hpMaskThreshold > 0 && maxHp > 0 && (hp / maxHp) < hpMaskThreshold && (
        <div className="map-hp-chip map-hp-chip--warn">
          <div className="map-hp-chip-icon map-hp-chip-icon--warn">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
              <line x1="12" y1="9" x2="12" y2="13" />
              <line x1="12" y1="17" x2="12.01" y2="17" />
            </svg>
          </div>
          <div className="map-hp-chip-body">
            <span className="map-hp-chip-name">Masked Hypoglycemia</span>
            <span className="map-hp-chip-sub">HP concealed below threshold</span>
          </div>
        </div>
      )}
      {isDead && (
        <div className="map-hp-chip map-hp-chip--danger">
          <div className="map-hp-chip-icon map-hp-chip-icon--danger">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 2C7.03 2 3 6.03 3 11c0 2.76 1.23 5.23 3.17 6.9V21a1 1 0 001 1h2a1 1 0 001-1v-1h1.5v1a1 1 0 001 1h2a1 1 0 001-1v-3.1C17.77 16.23 21 13.76 21 11c0-4.97-4.03-9-9-9z" />
              <circle cx="9" cy="11" r="1.5" />
              <circle cx="15" cy="11" r="1.5" />
            </svg>
          </div>
          <div className="map-hp-chip-body">
            <span className="map-hp-chip-name">Knocked Out</span>
            <span className="map-hp-chip-sub">Revive at a Resuscitate node</span>
          </div>
        </div>
      )}
    </div>
  ) : null;

  const wandPopover = conceptBuilder.active ? (
    <div className="bls-action-popover bls-action-popover--wand" role="menu">
      <button
        type="button"
        role="menuitem"
        className="map-upload-chip map-upload-chip--wand map-upload-chip--forge"
        onClick={() => conceptBuilder.setBuilderModalOpen(true)}
        disabled={!conceptBuilder.selections || conceptBuilder.selections.length === 0}
      >
        <div className="map-upload-chip-icon">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
          </svg>
        </div>
        <div className="map-upload-chip-body">
          <span className="map-upload-chip-name">Forge</span>
          <span className="map-upload-chip-sub">
            {(!conceptBuilder.selections || conceptBuilder.selections.length === 0)
              ? 'Highlight to enable'
              : 'New concept card'}
          </span>
        </div>
      </button>
      <button
        type="button"
        role="menuitem"
        className="map-upload-chip map-upload-chip--wand map-upload-chip--vault"
        onClick={() => conceptBuilder.setVaultOpen(true)}
      >
        <div className="map-upload-chip-icon">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="3" width="7" height="7" rx="1" />
            <rect x="14" y="3" width="7" height="7" rx="1" />
            <rect x="3" y="14" width="7" height="7" rx="1" />
            <rect x="14" y="14" width="7" height="7" rx="1" />
          </svg>
        </div>
        <div className="map-upload-chip-body">
          <span className="map-upload-chip-name">Vault</span>
          <span className="map-upload-chip-sub">Concept library</span>
        </div>
      </button>
      <button
        type="button"
        role="menuitem"
        className="map-upload-chip map-upload-chip--wand map-upload-chip--cheatsheet"
        onClick={() => conceptBuilder.setCheatSheetEditor({ card: null, initialContext: activeContext })}
      >
        <div className="map-upload-chip-icon">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
            <path d="M14 2v6h6" />
            <path d="M10 13h4" />
            <path d="M10 17h4" />
            <path d="M8 9h1" />
          </svg>
        </div>
        <div className="map-upload-chip-body">
          <span className="map-upload-chip-name">Cheat Sheet</span>
          <span className="map-upload-chip-sub">Editor</span>
        </div>
      </button>
    </div>
  ) : null;

  return createPortal(
    <div className="corner-dock">
      <div className="corner-dock-shell">
        {actionPopover}
        {wandPopover}
        {resolvedTab && resolvedTab !== 'achievements' && (
          <div className={`bls-tab-panel bls-tab-panel--${resolvedTab}`}>
            <div className="bls-tab-panel-body">
              {resolvedTab === 'profile' && renderProfileContent()}
              {resolvedTab === 'quests' && renderQuestContent()}
            </div>
          </div>
        )}

        <div className="corner-dock-actions">
          <button
            type="button"
            className={`bls-status-header bls-status-header--action bls-status-header--upload${actionTab === 'upload' ? ' is-active' : ''}`}
            onClick={() => toggleActionTab('upload')}
            aria-expanded={actionTab === 'upload'}
            aria-label="Upload options"
          >
            <svg className="bls-status-header-icon" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="17 8 12 3 7 8" />
              <line x1="12" y1="3" x2="12" y2="15" />
            </svg>
            <span className="bls-status-action-label">Upload</span>
          </button>
          <button
            type="button"
            className={`bls-status-header bls-status-header--action bls-status-header--wand${conceptBuilder.active ? ' is-active' : ''}${hasForgeable ? ' has-glow' : ''}`}
            onClick={() => {
              if (conceptBuilder.active) {
                conceptBuilder.setActive(false);
                conceptBuilder.clearSelections();
              } else {
                conceptBuilder.setActive(true);
              }
            }}
            aria-haspopup="menu"
            aria-expanded={conceptBuilder.active}
            aria-label={conceptBuilder.active ? 'Close concept tray' : 'Open concept tray'}
          >
            <svg className="bls-status-header-icon" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M15 4V2M15 8V6M8 2h5M19 15h2M19 19h2M22 17h-5M5 14l8 8 7-7-8-8-7 7z" />
              <path d="M9 18l6-6" />
            </svg>
            <span className="bls-status-action-label">Wand</span>
          </button>
        </div>

        <div className="corner-dock-bars">
          <button
            type="button"
            className={`corner-dock-bar corner-dock-bar--hp${isDead ? ' is-dead' : ''}${actionTab === 'hp' ? ' is-active' : ''}${overworldInvincible ? ' is-invincible' : ''}`}
            onClick={() => toggleActionTab('hp')}
            aria-expanded={actionTab === 'hp'}
            aria-label={overworldInvincible ? 'Indigo Mantle: overworld invincibility' : `HP ${hp}/${maxHp}`}
          >
            <svg className="corner-dock-bar-icon" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
              <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78L12 21.23l8.84-8.84a5.5 5.5 0 0 0 0-7.78z" />
            </svg>
            <div className="corner-dock-bar-track">
              <div
                className="corner-dock-bar-fill corner-dock-bar-fill--hp"
                style={{ width: `${overworldInvincible ? 100 : (maxHp > 0 ? Math.min(100, (hp / maxHp) * 100) : 0)}%` }}
              />
            </div>
            <span className="corner-dock-bar-value corner-dock-bar-value--hp">
              {overworldInvincible ? '∞' : `${hp}/${maxHp}`}
            </span>
          </button>
          <div
            className="corner-dock-bar corner-dock-bar--xp"
            role="img"
            aria-label={`XP ${xpCurrent}/${xpNeeded}, ${xpPct}%`}
          >
            <svg className="corner-dock-bar-icon" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 2 L14.5 8.5 L21 9 L16 13.5 L17.5 20 L12 16.5 L6.5 20 L8 13.5 L3 9 L9.5 8.5 Z" />
            </svg>
            <div className="corner-dock-bar-track">
              <div
                className="corner-dock-bar-fill corner-dock-bar-fill--xp"
                style={{ width: `${xpPct}%` }}
              />
            </div>
            <span className="corner-dock-bar-value corner-dock-bar-value--xp">{xpCurrent}/{xpNeeded}</span>
          </div>
        </div>

        <div className="corner-dock-tabs">
          <button
            type="button"
            className={`bls-status-header bls-status-header--tab bls-status-header--quests${resolvedTab === 'quests' ? ' is-active' : ''}`}
            onClick={() => toggleTab('quests')}
            aria-pressed={resolvedTab === 'quests'}
            aria-label={`Daily quests: ${dailyQuests.filter((q) => !q.completed).length || dailyQuests.length} open`}
          >
            <svg className="bls-status-header-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="9 11 12 14 22 4" />
              <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
            </svg>
            <span className="bls-status-tab-value">{dailyQuests.filter((q) => !q.completed).length || dailyQuests.length}</span>
          </button>
          <div
            className="bls-status-header bls-status-header--tab bls-status-header--ach"
            aria-label={`Honor: ${honorDisplay}`}
          >
            <svg className="bls-status-header-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <polygon points="12 2 15 8 22 9 17 14 18 21 12 18 6 21 7 14 2 9 9 8 12 2" />
            </svg>
            <span className="bls-status-tab-value">{honorDisplay}</span>
          </div>
          <button
            type="button"
            className={`bls-status-header bls-status-header--tab bls-status-header--profile${resolvedTab === 'profile' ? ' is-active' : ''}`}
            onClick={() => toggleTab('profile')}
            aria-pressed={resolvedTab === 'profile'}
            aria-label={`Profile: Level ${level}`}
          >
            <svg className="bls-status-header-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="8" r="4" />
              <path d="M4 21v-1a6 6 0 0 1 6-6h4a6 6 0 0 1 6 6v1" />
            </svg>
            <span className="bls-status-tab-value">{level}</span>
          </button>
          <div
            className={`bls-status-header bls-status-header--tab bls-status-header--elixir${animating ? ' is-animating' : ''}`}
            aria-label={`Elixir ${tokens}`}
          >
            <TokenIcon size={13} />
            <span className="bls-status-tab-value">
              <ElixirAmount value={tokens} />
            </span>
          </div>
        </div>
      </div>
    </div>,
    portalTarget
  );
}

BottomLeftStatusRail.displayName = 'BottomLeftStatusRail';

export default BottomLeftStatusRail;
